import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import {
  MapPin, Plus, Save, Trash2, ChevronLeft, ChevronRight, Check, Loader2,
  X, Flag, Target, Edit3, Eye
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';

const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function getAuthHeaders() {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, 18, { duration: 1 }); }, [center, map]);
  return null;
}

export default function CourseMapper() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeHole, setActiveHole] = useState(1);
  const [placingMode, setPlacingMode] = useState(null); // 'tee' | 'green' | null
  const [flyTarget, setFlyTarget] = useState(null);

  // New course form
  const [formName, setFormName] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formState, setFormState] = useState('');
  const [formHoles, setFormHoles] = useState(
    Array.from({ length: 18 }, (_, i) => ({
      number: i + 1, par: 4, yards: 350,
      tee: { lat: null, lng: null },
      green: { lat: null, lng: null },
    }))
  );

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await fetch(`${API}/smart-round/courses`);
      if (res.ok) { const d = await res.json(); setCourses(d.courses || []); }
    } catch {}
  };

  const loadCourse = async (courseId) => {
    try {
      const res = await fetch(`${API}/smart-round/courses/${courseId}`);
      if (res.ok) {
        const c = await res.json();
        setSelectedCourse(c);
        setFormName(c.name);
        setFormCity(c.city);
        setFormState(c.state);
        setFormHoles(c.holes.map(h => ({ ...h })));
        setActiveHole(1);
        const h1 = c.holes[0];
        if (h1?.tee?.lat) setFlyTarget([h1.tee.lat, h1.tee.lng]);
        else if (c.center?.lat) setFlyTarget([c.center.lat, c.center.lng]);
      }
    } catch {}
  };

  const startNewCourse = () => {
    setCreating(true);
    setEditMode(true);
    setSelectedCourse(null);
    setFormName('');
    setFormCity('');
    setFormState('');
    setFormHoles(Array.from({ length: 18 }, (_, i) => ({
      number: i + 1, par: 4, yards: 350,
      tee: { lat: null, lng: null },
      green: { lat: null, lng: null },
    })));
    setActiveHole(1);
  };

  const handleMapClick = useCallback((latlng) => {
    if (!placingMode) return;
    const { lat, lng } = latlng;
    setFormHoles(prev => prev.map(h => {
      if (h.number !== activeHole) return h;
      if (placingMode === 'tee') return { ...h, tee: { lat, lng } };
      if (placingMode === 'green') return { ...h, green: { lat, lng } };
      return h;
    }));
    // Auto-switch: tee -> green -> next hole tee
    if (placingMode === 'tee') {
      setPlacingMode('green');
    } else {
      setPlacingMode(null);
    }
  }, [placingMode, activeHole]);

  const updateHoleField = (holeNum, field, value) => {
    setFormHoles(prev => prev.map(h =>
      h.number === holeNum ? { ...h, [field]: value } : h
    ));
  };

  const saveCourse = async () => {
    if (!formName) return;
    setSaving(true);
    // Calculate center from all coordinates
    const allLats = formHoles.flatMap(h => [h.tee?.lat, h.green?.lat]).filter(Boolean);
    const allLngs = formHoles.flatMap(h => [h.tee?.lng, h.green?.lng]).filter(Boolean);
    const center = allLats.length > 0
      ? { lat: allLats.reduce((a, b) => a + b, 0) / allLats.length, lng: allLngs.reduce((a, b) => a + b, 0) / allLngs.length }
      : {};

    try {
      const res = await fetch(`${API}/smart-round/admin/courses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          course_id: selectedCourse?.course_id || undefined,
          name: formName,
          city: formCity,
          state: formState,
          center,
          holes: formHoles.filter(h => h.tee?.lat || h.green?.lat),
          tee_boxes: ['white', 'blue', 'gold', 'red'],
        })
      });
      if (res.ok) {
        setEditMode(false);
        setCreating(false);
        fetchCourses();
      }
    } catch {} finally { setSaving(false); }
  };

  const goToHole = (num) => {
    setActiveHole(num);
    const hole = formHoles.find(h => h.number === num);
    if (hole?.tee?.lat) setFlyTarget([hole.tee.lat, hole.tee.lng]);
    else if (hole?.green?.lat) setFlyTarget([hole.green.lat, hole.green.lng]);
  };

  const currentHoleData = formHoles.find(h => h.number === activeHole);
  const mappedCount = formHoles.filter(h => h.tee?.lat && h.green?.lat).length;

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white flex items-center justify-center">
        {user === null ? (
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        ) : (
          <p className="text-gray-400">Director access required</p>
        )}
      </div>
    );
  }

  // ==================== COURSE LIST ====================
  if (!editMode && !selectedCourse) {
    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-black" data-testid="course-mapper-title">Course Mapper</h1>
              <p className="text-sm text-gray-400">Add and edit GPS course data</p>
            </div>
            <Button
              data-testid="add-course-btn"
              onClick={startNewCourse}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Course
            </Button>
          </div>

          <div className="space-y-3">
            {courses.map(c => (
              <div
                key={c.course_id}
                className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all"
              >
                <div>
                  <div className="font-bold">{c.name}</div>
                  <div className="text-sm text-gray-400">{c.city}, {c.state}</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { loadCourse(c.course_id); }}
                    className="border-white/10 text-gray-400 hover:bg-white/5"
                  >
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => { loadCourse(c.course_id); setEditMode(true); }}
                    className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                  >
                    <Edit3 className="w-4 h-4 mr-1" /> Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ==================== VIEW / EDIT MODE ====================
  return (
    <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => { setSelectedCourse(null); setEditMode(false); setCreating(false); }}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          {editMode && (
            <Button
              data-testid="save-course-btn"
              onClick={saveCourse}
              disabled={saving || !formName}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Course
            </Button>
          )}
          {!editMode && selectedCourse && (
            <Button
              onClick={() => setEditMode(true)}
              className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
            >
              <Edit3 className="w-4 h-4 mr-2" /> Edit
            </Button>
          )}
        </div>

        {/* Course Info */}
        {editMode ? (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <input
              data-testid="course-name-input"
              value={formName}
              onChange={e => setFormName(e.target.value)}
              placeholder="Course Name"
              className="col-span-3 sm:col-span-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50"
            />
            <input
              value={formCity}
              onChange={e => setFormCity(e.target.value)}
              placeholder="City"
              className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50"
            />
            <input
              value={formState}
              onChange={e => setFormState(e.target.value)}
              placeholder="State"
              className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50"
            />
          </div>
        ) : (
          <div className="mb-4">
            <h2 className="text-xl font-black">{formName}</h2>
            <p className="text-sm text-gray-400">{formCity}, {formState} — {mappedCount}/{formHoles.length} holes mapped</p>
          </div>
        )}

        {/* Map */}
        <div className="rounded-2xl overflow-hidden border border-white/10 mb-4 h-[350px] relative" data-testid="course-mapper-map">
          <MapContainer
            center={flyTarget || [38.87, -77.03]}
            zoom={17}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
            {flyTarget && <FlyTo center={flyTarget} />}
            {editMode && <MapClickHandler onMapClick={handleMapClick} />}

            {/* Render all hole markers */}
            {formHoles.map(h => (
              <React.Fragment key={h.number}>
                {h.tee?.lat && (
                  <Marker position={[h.tee.lat, h.tee.lng]} icon={redIcon} />
                )}
                {h.green?.lat && (
                  <>
                    <Marker position={[h.green.lat, h.green.lng]} icon={greenIcon} />
                    <Circle
                      center={[h.green.lat, h.green.lng]}
                      radius={20}
                      pathOptions={{
                        color: h.number === activeHole ? '#10b981' : '#065f46',
                        fillColor: '#10b981',
                        fillOpacity: h.number === activeHole ? 0.4 : 0.1
                      }}
                    />
                  </>
                )}
              </React.Fragment>
            ))}
          </MapContainer>

          {/* Placing Mode Indicator */}
          {placingMode && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 rounded-full bg-emerald-500 text-black font-bold text-sm shadow-lg">
              Click map to place {placingMode === 'tee' ? 'TEE BOX' : 'GREEN'} for Hole {activeHole}
            </div>
          )}
        </div>

        {/* Hole Navigation */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
          {formHoles.map(h => {
            const hasTee = !!h.tee?.lat;
            const hasGreen = !!h.green?.lat;
            const complete = hasTee && hasGreen;
            return (
              <button
                key={h.number}
                onClick={() => goToHole(h.number)}
                className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all ${
                  h.number === activeHole
                    ? 'bg-emerald-500 text-black'
                    : complete
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : hasTee || hasGreen
                        ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                        : 'bg-white/5 text-gray-500 border border-white/10'
                }`}
              >
                {h.number}
              </button>
            );
          })}
        </div>

        {/* Hole Detail Panel */}
        {currentHoleData && (
          <div className="bg-white/5 rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Hole {activeHole}</h3>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className={currentHoleData.tee?.lat ? 'text-emerald-400' : 'text-gray-600'}>
                  {currentHoleData.tee?.lat ? 'Tee set' : 'No tee'}
                </span>
                <span>|</span>
                <span className={currentHoleData.green?.lat ? 'text-emerald-400' : 'text-gray-600'}>
                  {currentHoleData.green?.lat ? 'Green set' : 'No green'}
                </span>
              </div>
            </div>

            {editMode && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Par</label>
                    <select
                      value={currentHoleData.par}
                      onChange={e => updateHoleField(activeHole, 'par', parseInt(e.target.value))}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white"
                    >
                      {[3, 4, 5].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Yards</label>
                    <input
                      type="number"
                      value={currentHoleData.yards}
                      onChange={e => updateHoleField(activeHole, 'yards', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setPlacingMode(placingMode === 'tee' ? null : 'tee')}
                    className={`flex-1 ${placingMode === 'tee' ? 'bg-red-500 text-white' : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'}`}
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    {placingMode === 'tee' ? 'Placing Tee...' : 'Set Tee Box'}
                  </Button>
                  <Button
                    onClick={() => setPlacingMode(placingMode === 'green' ? null : 'green')}
                    className={`flex-1 ${placingMode === 'green' ? 'bg-emerald-500 text-black' : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'}`}
                  >
                    <Target className="w-4 h-4 mr-2" />
                    {placingMode === 'green' ? 'Placing Green...' : 'Set Green'}
                  </Button>
                </div>
              </>
            )}

            {!editMode && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="text-gray-500 text-xs mb-1">Par</div>
                  <div className="font-bold">{currentHoleData.par}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="text-gray-500 text-xs mb-1">Yards</div>
                  <div className="font-bold">{currentHoleData.yards}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
